import {I18n} from 'vue-i18n'
import {AuthenticationService} from '@/service/authentication/AuthenticationService'
import {
  siteManagerOrderArraySchema,
  SiteManagerOrderBean,
  siteManagerOrderSchema
} from '@/service/model/sitemanager/SiteManagerOrderBean'
import {CustomerOrderBySiteBean, CustomerOrderDetailBean, MaterialDeliveryNoteBean} from '@/service/model/sitemanager/CustomerOrderBean'
import {deliveryNoteArraySchema, DeliveryNoteBean} from '@/service/model/DeliveryNoteBean'
import {PictureBean} from '@/service/model/PictureBean'
import {AbstractPictureApi, PICTURE_REQUEST_TIME} from '@/service/api/AbstractPictureApi'
import {SynchronizationService} from '@/service/SynchronizationService'
import _ from 'lodash'

export const TimePreference = {
  AM: 'AM',
  PM: 'PM',
  NONE: 'NONE'
}

export interface Location {
  latitude: number
  longitude: number
}
/*
export interface SiteManagerDeliveryRequestBean {
  material_id: string
  backload_material_id?: string
  day: string
  time_preference: string
  trip_count: number
  comment?: string
  trucksType?: string
  quantity?: number
}
*/

export interface SiteManagerDeliveryRequestMaterialV2Bean {
  orderMaterialId: string
  materialId?: string | null
  materialType: string | null
  materialName: string | null
  offeredMaterialName?: string | null
  quarryMaterialName?: string | null
  quarryMaterialCode?: string | null
  materialUnit: string | null
  unitPrice?: number | null
  quantity: number
  deliveredQuantity?: number | null
  quantityDelivered?: number | null
  quantityConfirmed?: number | null
}

export interface SiteManagerDeliveryRequestBean {
  id?: string | null
  reference?: string | null
  orderId?: string
  siteManagerId?: string
  deliveryDate?: string
  timeSlotDeliveryStartTime?: string
  timeSlotDeliveryEndTime?: string | null
  responseDelay?: number | null
  quantity?: number
  truckType?: string
  doubleFreight?: boolean
  comment?: string
  status?: string
  backloadDeliveryOnly?: boolean
  materialDeliveryOnly?: boolean
  materialDeliveryAndBackloadReturn?: boolean
  address1?: string
  address2?: string
  postalCode?: string
  city?: string
  location?: Location
  inDispute?: boolean | null
  decisionComment?: string | null
  disputeComment?: string | null
  disputeReasons?: Array<string> | null
  hasAutomaticDeliveryNoteIntegration?: boolean | null
  deliveryNotes?: DeliveryNoteBean[] | null
  materialNetWeight?: MaterialNetWeight | null
  deliveryRequestType?: string | null
  materialName?: string | null
  materialUnit?: string | null
  quarryName?: string | null
  materials?: SiteManagerDeliveryRequestMaterialV2Bean[]
}

export interface MaterialNetWeight {
  materialPercentage?: number | null
  materialNetWeight?: number | null
  wastePercentage?: number | null
  wasteNetWeight?: number | null
}

export interface DisputeDto {
  disputeReasons?: string | null
  disputeComment?: string | null
}

export interface ReasonDto {
  cancelReason: string
}

export interface DeliveryRequestMaterialDto {
  orderMaterialId: string
  materialType: string
  quantity: number
}

export interface CreateDeliveryRequestV2Bean {
  orderId: string
  materials: DeliveryRequestMaterialDto[]
  deliveryDate: string        // YYYY-MM-DD
  responseDelay: number       // hours
  address1: string
  address2?: string | null
  postalCode: string
  city: string
  location?: { latitude: number; longitude: number } | null
  deliveryRequestType: 'DELIVERY' | 'PICKUP'
  truckType?: string | null
  timeSlotDeliveryStartTime?: string | null  // HH:mm
  timeSlotDeliveryEndTime?: string | null    // HH:mm
  comment?: string | null
}

export interface MaterialConfirmationDto {
  orderMaterialId: string
  quantityDelivered: number
  unitPrice?: number | null
  truckRegistration?: string | null
  blReference?: string | null
  blPhotoBase64?: string | null
  blPhotoContentType?: string | null
}

export interface ConfirmDeliveryRequestV2Bean {
  deliveryDate?: string | null
  deliveryDay?: string | null
  loadDate?: string | null
  date?: string | null
  timeSlotDeliveryStartTime?: string | null
  materials: MaterialConfirmationDto[]
}

export class SiteManagerOrderApi extends AbstractPictureApi {
  
  constructor(
    i18n: I18n,
    authenticationService: AuthenticationService,
    synchronizationService: SynchronizationService
  ) {
    super(i18n, authenticationService, synchronizationService)
  }
  
  async fetchOrders(): Promise<Array<SiteManagerOrderBean>> {
    return this.get<Array<SiteManagerOrderBean>>({
      path: '/api/mp/v1/site-manager/orders',
      authentication: 'required',
      schema: siteManagerOrderArraySchema
    })
  }

  async fetchOrdersV2(): Promise<Array<CustomerOrderBySiteBean>> {
    return this.get<Array<CustomerOrderBySiteBean>>({
      path: '/api/mp/v2/site-customer/orders',
      authentication: 'required',
    })
  }

  async fetchOrderByIdV2(id: string): Promise<CustomerOrderDetailBean> {
    const data = await this.get<any>({
      path: `/api/mp/v2/site-customer/orders/${id}`,
      authentication: 'required',
    })
    return {
      ...data,
      isExpressOrder: data.isExpressOrder ?? data.expressOrder ?? false,
    }
  }

  async fetchMaterialDeliveryNotes(orderId: string, materialId: string): Promise<Array<MaterialDeliveryNoteBean>> {
    return this.get<Array<MaterialDeliveryNoteBean>>({
      path: `/api/mp/v2/site-customer/orders/${orderId}/materials/${materialId}/delivery-notes`,
      authentication: 'required',
    })
  }
  
  async fetchOrder(id: string): Promise<SiteManagerOrderBean> {
    return this.get<SiteManagerOrderBean>({
      path: `/api/mp/v1/site-manager/orders/${id}`,
      authentication: 'required',
      schema: siteManagerOrderSchema
    })
  }
  
  async fetchPickupOrders(): Promise<Array<SiteManagerOrderBean>> {
    return this.get<Array<SiteManagerOrderBean>>({
      path: '/api/mp/v1/site-manager/orders/pick-up',
      authentication: 'required',
      schema: siteManagerOrderArraySchema
    })
  }
  
  async fetchDeliveredOrders(): Promise<Array<SiteManagerOrderBean>> {
    return this.get<Array<SiteManagerOrderBean>>({
      path: '/api/mp/v1/site-manager/orders/delivered',
      authentication: 'required',
      schema: siteManagerOrderArraySchema
    })
  }
  
  async fetchDeliveryNotes(orderId: string): Promise<Array<DeliveryNoteBean>> {
    return this.get<Array<DeliveryNoteBean>>({
      path: `/api/mp/v2/site-customer/delivery-notes?orderId=${encodeURIComponent(orderId)}`,
      authentication: 'required',
      schema: deliveryNoteArraySchema
    })
  }

  async getDeliveryRequest(id: string): Promise<SiteManagerDeliveryRequestBean> {
    return this.get({
      path: `/api/mp/v1/delivery-request/${id}`,
      authentication: 'required'
    })
  }

  async getDeliveryRequestV2(id: string): Promise<SiteManagerDeliveryRequestBean> {
    return this.get({
      path: `/api/mp/v2/site-customer/delivery-requests/${id}`,
      authentication: 'required'
    })
  }

  async getDeliveryRequestWithDeliveryNote(params : {deliveryRequestId: string; materialId: string}): Promise<Array<DeliveryNoteBean>> {
    return this.post({
      path: 'api/mp/v1/delivery-request/link-to-delivery-note',
      authentication: 'required',
      body: params
    })
  }

  async confirmDelivery(deliveryRequestId: string): Promise<void> {
    return this.putVoid({
      path: `/api/mp/v1/delivery-request/${deliveryRequestId}/confirm-delivery`,
      authentication: 'required'
    })
  }

  async confirmDeliveryV2(deliveryRequestId: string, dto: ConfirmDeliveryRequestV2Bean): Promise<void> {
    return this.postVoid({
      path: `/api/mp/v2/site-customer/delivery-requests/${deliveryRequestId}/confirm`,
      authentication: 'required',
      body: dto,
    })
  }

  async requestDelivery(deliveryRequestDto: SiteManagerDeliveryRequestBean): Promise<void> {
    return this.postVoid({
      path: '/api/mp/v1/delivery-request',
      authentication: 'required',
      body: deliveryRequestDto
    })
  }

  async fetchDeliveryRequests(type: string): Promise<Array<SiteManagerDeliveryRequestBean>> {
    return this.get({
      path: `/api/mp/v1/delivery-request/site-manager/${type}`,
      authentication: 'required',
    })
  }
  
  async fetchDeliveryRequestByCode(code: string): Promise<SiteManagerDeliveryRequestBean> {
    return this.get({
      path: `/api/mp/v1/delivery-request/by-code/${code}`,
      authentication: 'required'
    })
  }

  async disputeDeliveryRequest(params: { id: string; disputeDto: DisputeDto; picture: PictureBean }): Promise<void> {
    const formData = new FormData()

    if (!_.isEmpty(params.picture.location)) {
      formData.set('gps-latitude', `${params.picture.location?.latitude}`)
      formData.set('gps-longitude', `${params.picture.location?.longitude}`)
    }
    if (!_.isEmpty(params.picture.date)) {
      formData.set('timestamp', params.picture.date as string)
    }

    await AbstractPictureApi.addPictureToFormData(formData, 'photo.jpg', params.picture.picture, 'photo')
    await AbstractPictureApi.addPictureToFormData(formData, 'photo.jpg', params.picture.picture, 'file')

    formData.set('disputeDto', new Blob([JSON.stringify(params.disputeDto)], { type: 'application/json' }))

    return this.postVoid({
      path: `/api/mp/v2/site-customer/delivery-requests/${params.id}/dispute`,
      authentication: 'required',
      rawBody: formData,
      requestTimeout: PICTURE_REQUEST_TIME
    })
  }

  async deleteOrder(params : {orderId: string; reason: ReasonDto}): Promise<void> {
    return this.putVoid({
      path: `/api/mp/v1/site-manager/orders/${params.orderId}/cancel`,
      authentication: 'required',
      body: params.reason
    })
  }

  async createDeliveryRequestV2(body: CreateDeliveryRequestV2Bean): Promise<{id: string}> {
    return this.post<{id: string}>({
      path: '/api/mp/v2/site-customer/delivery-requests',
      authentication: 'required',
      body: body
    })
  }

  async fetchAllDeliveryRequestsV2(orderId?: string): Promise<Array<SiteManagerDeliveryRequestBean>> {
    const params = orderId ? `?orderId=${encodeURIComponent(orderId)}` : ''
    return this.get({
      path: `/api/mp/v2/site-customer/delivery-requests${params}`,
      authentication: 'required',
    })
  }

}
